import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Building, Briefcase, UserCheck } from 'lucide-react';
interface InvestorSlide7Props {
  isDark?: boolean;
}
export function InvestorSlide7({
  isDark = false
}: InvestorSlide7Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-349">
      <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-350">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16 text-center" data-id="element-351">
          <h1 className={`text-3xl sm:text-5xl font-bold mb-6 sm:mb-8 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-352">
            Target Market
          </h1>
          <p className={`text-lg sm:text-2xl max-w-3xl mx-auto ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-353">
            Focused on organizations where leadership capacity is critical to
            success.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-16" data-id="element-354">
          {/* Primary Segment */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-heliona-mint/10 border border-heliona-mint/30' : 'bg-gradient-to-br from-heliona-mint/10 to-heliona-mint/5 border border-heliona-mint/30'}`} data-id="element-355">
            <div className="flex items-center gap-4 mb-6" data-id="element-356">
              <div className="p-3 rounded-xl bg-heliona-mint/20" data-id="element-357">
                <Building className="w-6 h-6 text-heliona-mint-dark" data-id="element-358" />
              </div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-359">
                Primary Segment
              </h3>
            </div>
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-360">
              Mid-Market Organizations
            </h4>
            <ul className={`space-y-3 text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-361">
              <li data-id="element-362">• 500 - 5,000 employees</li>
              <li data-id="element-363">• 50 - 500 people leaders</li>
              <li data-id="element-364">• High growth, high pressure environments</li>
            </ul>
          </motion.div>

          {/* Secondary Segment */}
          <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige shadow-sm'}`} data-id="element-365">
            <div className="flex items-center gap-4 mb-6" data-id="element-366">
              <div className="p-3 rounded-xl bg-heliona-navy/10" data-id="element-367">
                <Briefcase className="w-6 h-6 text-heliona-navy" data-id="element-368" />
              </div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-369">
                Secondary Segment
              </h3>
            </div>
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white/90' : 'text-heliona-charcoal/90'}`} data-id="element-370">
              Enterprise Organizations
            </h4>
            <ul className={`space-y-3 text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-371">
              <li data-id="element-372">• Seeking specialized leadership layer</li>
              <li data-id="element-373">• Complementing existing general EAP</li>
              <li data-id="element-374">
                • Verticals: Tech, Finance, Healthcare, Professional Services
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Buyer Personas */}
        <motion.div variants={itemVariants} className={`rounded-3xl p-8 sm:p-10 text-center ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-heliona-cream border border-heliona-beige'}`} data-id="element-375">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-376">
            <UserCheck className={`w-6 h-6 ${isDark ? 'text-heliona-orange' : 'text-heliona-orange'}`} data-id="element-377" />
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-378">
              Buyer Personas
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8" data-id="element-379">
            {['CHRO / Chief People Officer', 'VP of Talent Development', 'Head of Employee Experience'].map((persona, idx) => <span key={idx} className={`px-6 py-3 rounded-full text-sm sm:text-base font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-white text-heliona-charcoal shadow-sm'}`} data-id="element-380">
                {persona}
              </span>)}
          </div>
        </motion.div>
      </div>
    </motion.div>;
}